import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import {
  WelcomeStep, TourStep, RoomsStep, DoneStep, StepIndicator,
} from "@/components/OnboardingSteps";

const STORAGE_KEY = "xenia_onboarding_done_v2";

const blankRoom = () => ({ name: "", type: "doppia", capacity: 2, price_per_night: 80 });

/**
 * Onboarding wizard — 4 step:
 *   1) Welcome
 *   2) Quick tour (4 key features)
 *   3) Add first room(s) — multi-row support
 *   4) Done + CTAs (scan, calendar)
 *
 * Triggered automatically when the user has a property but zero rooms and the
 * localStorage flag is not set. Can be re-opened manually from the
 * OnboardingChecklist on the Dashboard by remounting with a fresh `key` prop.
 */
export default function OnboardingWizard({ initialOpen = false, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { current: property } = useProperty();
  const nav = useNavigate();

  const [show, setShow] = useState(initialOpen);
  const [step, setStep] = useState(1);
  const [rooms, setRooms] = useState([blankRoom()]);
  const [saving, setSaving] = useState(false);

  // Auto-open on first dashboard visit when host has no rooms yet.
  useEffect(() => {
    if (initialOpen) return;
    if (localStorage.getItem(STORAGE_KEY) || !property) return;
    let cancelled = false;
    api.get("/rooms").then(({ data }) => {
      if (!cancelled && (data || []).length === 0) setShow(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [property, initialOpen]);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
    onClose?.();
  };

  const updateRoom = (idx, patch) =>
    setRooms((arr) => arr.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const addRoomRow = () => setRooms((arr) => [...arr, blankRoom()]);

  const removeRoomRow = (idx) =>
    setRooms((arr) => (arr.length <= 1 ? arr : arr.filter((_, i) => i !== idx)));

  const saveRooms = async () => {
    const valid = rooms.filter((r) => r.name && r.name.trim() !== "");
    if (valid.length === 0) {
      toast.error(t("onboarding.err_room_name"));
      return;
    }
    setSaving(true);
    try {
      // Parallel create — wizard latency stays low even when adding many rooms.
      await Promise.all(valid.map((r) => api.post("/rooms", {
        name: r.name.trim(),
        type: r.type || "doppia",
        capacity: Number(r.capacity) || 1,
        price_per_night: Number(r.price_per_night) || 0,
      })));
      toast.success(t("onboarding.rooms_saved", { count: valid.length }));
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("onboarding.err_generic"));
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const labels = [
    t("onboarding.step_welcome"),
    t("onboarding.step_tour"),
    t("onboarding.step_rooms"),
    t("onboarding.step_done"),
  ];

  return (
    <div
      className="fixed inset-0 z-[100] bg-stone-950/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="onboarding-wizard"
    >
      <div className="bg-white max-w-3xl w-full rounded-sm shadow-2xl relative my-8">
        <button
          onClick={close}
          className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 p-2"
          data-testid="onboarding-close"
          aria-label={t("onboarding.close")}
        >
          <X className="w-5 h-5" />
        </button>

        <StepIndicator step={step} labels={labels} />

        {step === 1 && (
          <WelcomeStep
            t={t}
            userName={user?.full_name?.split(" ")[0] || ""}
            onNext={() => setStep(2)}
            onSkip={close}
          />
        )}
        {step === 2 && (
          <TourStep t={t} onBack={() => setStep(1)} onNext={() => setStep(3)} />
        )}
        {step === 3 && (
          <RoomsStep
            t={t}
            rooms={rooms}
            onUpdate={updateRoom}
            onAdd={addRoomRow}
            onRemove={removeRoomRow}
            onBack={() => setStep(2)}
            onSave={saveRooms}
            saving={saving}
          />
        )}
        {step === 4 && (
          <DoneStep
            t={t}
            onScan={() => { close(); nav("/app/scanner"); }}
            onCalendar={() => { close(); nav("/app/calendar"); }}
            onHome={close}
          />
        )}
      </div>
    </div>
  );
}
